import {
  Dialog,
  DialogClose,
  H3,
  Icon,
  P,
  Spacer,
  styled,
  theme,
} from 'design-system';

import { useAuthContext } from '../auth-context';
import { ForgotPasswordForm } from '../forms/forgot-password-form';
import { LoginForm } from '../forms/login-form';
import { SignUpForm } from '../forms/sign-up-form';

export type AuthScreen = 'login' | 'signup' | 'forgot-password' | 'default';

const AuthDialog = styled(Dialog, {
  maxWidth: '864px !important',
  width: '100%',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  overflow: 'hidden !important',
  maxHeight: '80vh',
  display: 'flex',
  variants: {
    size: {
      small: {
        width: '90vw',
        height: '90vh',
      },
    },
  },
});
const ScreenView = ({
  screen,
  goToScreen,
  isUserSignedUp,
  onSignIn,
  loading,
  onSignUp,
  editorialDesk = false,
  supportEmail,
}: {
  screen: AuthScreen;
  goToScreen: (screen: AuthScreen) => void;
  isUserSignedUp: boolean;
  onSignIn: () => void;
  loading: boolean;
  onSignUp?: (token: string) => void;
  editorialDesk?: boolean;
  supportEmail: string;
}) => {
  const loginForm = (
    <>
      <FormHeader>Sign in</FormHeader>
      <LoginForm
        onSignIn={onSignIn}
        wiring={{
          signUpLink: { onClick: () => goToScreen('signup') },
          forgotPassword: { onClick: () => goToScreen('forgot-password') },
        }}
        editorialDesk={editorialDesk}
        supportEmail={supportEmail}
      />
    </>
  );
  const signUpForm = (
    <>
      <FormHeader>Create account</FormHeader>
      <SignUpForm
        loading={loading}
        onSignUp={onSignUp}
        onSignIn={onSignIn}
        wiring={{
          signInLink: {
            onClick: () => goToScreen('login'),
          },
        }}
        supportEmail={supportEmail}
      />
    </>
  );
  switch (screen) {
    case 'forgot-password':
      return (
        <>
          <FormHeader>Forgot password?</FormHeader>
          <ForgotPasswordForm
            onSignIn={onSignIn}
            wiring={{ goToLogin: { onClick: () => goToScreen('login') } }}
          />
        </>
      );
    case 'login':
      return loginForm;
    case 'signup':
      return signUpForm;
    case 'default':
      return isUserSignedUp ? loginForm : signUpForm;
  }
};

export const UserDialog = (props: {
  hideClose?: boolean;
  screen: AuthScreen;
  open: boolean;
  setOpen: (openDialog: boolean) => void;
  setScreen: (screen: AuthScreen) => void;
  onSignIn?: () => void;
  onSignUp?: (token: string) => void;
  loading?: boolean;
  editorialDesk?: boolean;
  leftSectionLogo?: string;
  optionalLanguageTextUserDialog?: {
    create_a_free_paperpal_account?: string;
    iso_iec_certified_data_security?: string;
    powerful_ai_technology?: string;
    one_set_of_credentials?: string;
    if_you_have_any_questions?: string;
  };
  supportEmail?: string;
}) => {
  const {
    screen,
    open,
    setOpen,
    setScreen,
    onSignIn,
    optionalLanguageTextUserDialog,
    supportEmail = 'preflight-support@paperpal.com',
  } = props;
  const { isUserSignedUp } = useAuthContext();
  const hideClose = props.hideClose ?? false;
  const editorialDesk = props.editorialDesk ?? false;

  return (
    <AuthDialog size={{ '@sm': 'small' }} open={open} setOpen={setOpen}>
      {!hideClose && <DialogClose onClick={() => setOpen(false)} />}
      <LeftContainer size={{ '@sm': 'small' }}>
        {editorialDesk ? (
          <LeftSideLayout
            style={{ flexDirection: 'row', alignItems: 'center' }}
          >
            <img src={props.leftSectionLogo ?? ''} alt="Preflight-Logo" />
          </LeftSideLayout>
        ) : (
          <LeftSideLayout>
            <H3 as={'h1'}>
              {optionalLanguageTextUserDialog?.create_a_free_paperpal_account ||
                'Create a free Paperpal account'}
            </H3>
            <Spacer height={24} />
            <BenefitList>
              <BenefitItem>
                <IconContainer color={'yellow'}>
                  <Icon variant={'solid'} name={'shieldHalved'} />
                </IconContainer>
                <BenefitLabel>
                  {optionalLanguageTextUserDialog?.iso_iec_certified_data_security ||
                    'ISO/IEC 27001:2013 certified data security protocols that protect your work'}
                </BenefitLabel>
              </BenefitItem>
              <BenefitItem>
                <IconContainer color={'blue'}>
                  <Icon variant={'solid'} name={'file'} />
                </IconContainer>
                <BenefitLabel>
                  {optionalLanguageTextUserDialog?.powerful_ai_technology ||
                    'Powerful AI technology to elevate your research writing'}
                </BenefitLabel>
              </BenefitItem>
              <BenefitItem>
                <IconContainer color={'green'}>
                  <Icon variant={'solid'} name={'circleEllipsis'} />
                </IconContainer>
                <BenefitLabel>
                  {optionalLanguageTextUserDialog?.one_set_of_credentials ||
                    'One set of credentials to access the full product suite'}
                </BenefitLabel>
              </BenefitItem>
            </BenefitList>
            <Spacer height={24} />
            <LeftSideBottom>
              <P size={'small'}>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      optionalLanguageTextUserDialog?.if_you_have_any_questions ||
                      `If you have any questions contact us at&nbsp;
                <a href=mailto:${supportEmail}>${supportEmail}</a>`,
                  }}
                />
                {/* {optionalLanguageTextUserDialog?.if_you_have_any_questions} */}
                {/* If you have any questions contact us at&nbsp;
                <a href={'mailto:hello@paperpal.com'}>hello@paperpal.com</a> */}
              </P>
            </LeftSideBottom>
          </LeftSideLayout>
        )}
      </LeftContainer>
      <RightContainer size={{ '@sm': 'small' }}>
        <ScreenView
          loading={props.loading ?? false}
          onSignUp={props.onSignUp}
          screen={screen}
          goToScreen={setScreen}
          isUserSignedUp={isUserSignedUp}
          onSignIn={onSignIn ?? (() => {})}
          editorialDesk={editorialDesk}
          supportEmail={supportEmail}
        />
      </RightContainer>
    </AuthDialog>
  );
};

const LeftSideLayout = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: '33px 40px 48px 40px',
});

const LeftSideBottom = styled('div', {
  marginTop: 'auto',
  paddingTop: 8,
  borderTop: `1px solid ${theme.colors.gray7}`,
});

const LeftContainer = styled('div', {
  width: 350,
  backgroundColor: theme.colors.gray4,
  display: 'flex',
  flexShrink: 0,
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  variants: {
    size: {
      small: {
        display: 'none',
      },
    },
  },
});

const RightContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  overflow: 'auto',
  flex: 1,
  padding: `33px 40px 48px 40px`,
  variants: {
    size: {
      small: {
        padding: '1rem',
      },
    },
  },
});

const BenefitLabel = styled(P, {
  color: theme.colors.blue12,
});

const BenefitList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const BenefitItem = styled('div', {
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
});

const IconContainer = styled('div', {
  borderRadius: 4,
  flexShrink: 0,
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ['& svg']: {
    height: 16,
  },
  variants: {
    color: {
      yellow: {
        backgroundColor: theme.colors.yellow5,
        color: theme.colors.yellow11,
      },
      green: {
        backgroundColor: theme.colors.green5,
        color: theme.colors.green10,
      },
      blue: {
        backgroundColor: theme.colors.blue5,
        color: theme.colors.blue10,
      },
    },
  },
});

const FormHeader = styled(H3, {
  paddingBottom: 32,
});
